import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Zap, ChevronLeft, ChevronRight, Copy, Printer, RefreshCw, Eye, EyeOff, CheckCircle2, Layers, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["Datos Base", "Comisiones y Cupos", "Taquillas y Credenciales", "Resumen"];
const CURS = ["BS", "USD", "COP"];
const NO_CLONE = "__none__";
const pad = (n) => String(n).padStart(2, "0");

export const genPassword = () => {
  const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnpqrstuvwxyz", "23456789", "*#$%!@"];
  const all = sets.join("");
  const rnd = (s) => s[crypto.getRandomValues(new Uint32Array(1))[0] % s.length];
  const chars = [...sets.map(rnd), ...Array.from({ length: 8 }, () => rnd(all))];
  return chars.sort(() => (crypto.getRandomValues(new Uint8Array(1))[0] % 2 ? 1 : -1)).join("");
};

const buildUnits = (code, count, prev = []) => Array.from({ length: count }, (_, i) => {
  const c = (code || "AGE").trim();
  const p = prev[i] || {};
  return {
    name: p.name || `Taquilla ${pad(i + 1)}`, code: `${c}-${pad(i + 1)}`,
    username: `${c.toLowerCase()}_caja${i + 1}`, password: p.password || genPassword(), full_name: p.full_name || `Cajero ${pad(i + 1)}`,
  };
});

const EMPTY_BASE = { name: "", code: "", grupo_id: "", currencies: ["BS"], sales_limit_bs: "", clone_from: NO_CLONE };
const EMPTY_MADRE = { create: true, username: "", password: genPassword(), full_name: "Supervisor Caja 00" };

function StepBase({ base, setBase, grupos, agencias }) {
  const grupo = grupos.find((g) => g.id === base.grupo_id);
  const allowed = grupo?.currencies?.length ? grupo.currencies : ["BS", "USD"];
  const cloneOptions = agencias.filter((a) => !base.grupo_id || a.grupo_id === base.grupo_id || a.banca_id === grupo?.banca_id);
  const toggleCur = (c) => setBase((b) => ({ ...b, currencies: b.currencies.includes(c) ? b.currencies.filter((x) => x !== c) : [...b.currencies, c] }));
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="agencia-plus-step-1">
      <div className="space-y-1.5"><Label>Nombre de la agencia</Label>
        <Input value={base.name} onChange={(e) => setBase({ ...base, name: e.target.value })} placeholder="Agencia Centro" data-testid="agencia-plus-name-input" /></div>
      <div className="space-y-1.5"><Label>Código único</Label>
        <Input value={base.code} onChange={(e) => setBase({ ...base, code: e.target.value.toUpperCase().replace(/\s+/g, "") })} placeholder="AGC01" className="font-mono" data-testid="agencia-plus-code-input" /></div>
      <div className="space-y-1.5"><Label>Grupo padre</Label>
        <Select value={base.grupo_id} onValueChange={(v) => setBase({ ...base, grupo_id: v, currencies: ["BS"], clone_from: NO_CLONE })}>
          <SelectTrigger data-testid="agencia-plus-grupo-select"><SelectValue placeholder="Selecciona un grupo" /></SelectTrigger>
          <SelectContent>{grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {g.code}</SelectItem>)}</SelectContent>
        </Select></div>
      <div className="space-y-1.5"><Label>Límite de venta diario (Bs, opcional)</Label>
        <Input type="number" min="0" value={base.sales_limit_bs} onChange={(e) => setBase({ ...base, sales_limit_bs: e.target.value })} placeholder="Ilimitado" data-testid="agencia-plus-limit-input" /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Divisas operativas</Label>
        <div className="flex flex-wrap gap-2">{CURS.map((c) => {
          const on = base.currencies.includes(c); const dis = !allowed.includes(c);
          return <button key={c} type="button" disabled={dis} onClick={() => toggleCur(c)} data-testid={`agencia-plus-currency-${c}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/60"} ${dis ? "opacity-40 cursor-not-allowed" : ""}`}>{c}</button>;
        })}</div></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Clonar configuración desde…</Label>
        <Select value={base.clone_from} onValueChange={(v) => setBase({ ...base, clone_from: v })}>
          <SelectTrigger data-testid="agencia-plus-clone-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_CLONE}>No clonar (configurar manualmente)</SelectItem>
            {cloneOptions.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.code}</SelectItem>)}
          </SelectContent>
        </Select></div>
    </div>
  );
}

function StepConfig({ base, cfg, setCfg, agencias }) {
  const [show, setShow] = useState(false);
  const src = agencias.find((a) => a.id === base.clone_from);
  const { data: srcCfg, isError } = useQuery({
    queryKey: ["entity-config", "agencia", src?.id], enabled: !!src && show,
    queryFn: async () => (await api.get(`/entity-config/agencia/${src.id}`)).data, retry: false,
  });
  if (src) {
    return (
      <div className="space-y-3" data-testid="agencia-plus-step-2">
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3">
          <Layers size={18} className="text-primary" />
          <span className="text-sm font-medium" data-testid="agencia-plus-inherited-badge">Configuración heredada de {src.name}</span>
          <span className="text-xs text-muted-foreground">Comisión {src.commission_pct}%</span>
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setShow((s) => !s)} data-testid="agencia-plus-view-inherited">
            {show ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}{show ? "Ocultar" : "Ver configuración"}</Button>
        </div>
        {show && <pre className="text-xs rounded-lg border border-border bg-muted/40 p-3 max-h-56 overflow-auto" data-testid="agencia-plus-inherited-json">
          {isError ? "No tienes permisos para visualizar el detalle; se copiará íntegramente al crear." : JSON.stringify(srcCfg?.config || {}, null, 2)}</pre>}
        <p className="text-xs text-muted-foreground">Solo se heredan los cortes de las divisas habilitadas en la nueva agencia ({base.currencies.join(", ")}).</p>
      </div>
    );
  }
  const setCur = (c, k, v) => setCfg((x) => ({ ...x, per: { ...x.per, [c]: { ...(x.per[c] || {}), [k]: v } } }));
  return (
    <div className="space-y-4" data-testid="agencia-plus-step-2">
      <div className="space-y-1.5 max-w-xs"><Label>Comisión general (%)</Label>
        <Input type="number" min="0" max="100" value={cfg.commission_pct} onChange={(e) => setCfg({ ...cfg, commission_pct: e.target.value })} data-testid="agencia-plus-commission-input" /></div>
      <div className="grid sm:grid-cols-3 gap-3">{base.currencies.map((c) => (
        <div key={c} className="rounded-lg border border-border p-3 space-y-2">
          <div className="text-xs font-semibold tracking-widest text-primary">{c}</div>
          <Label className="text-xs">Participación (%)</Label>
          <Input type="number" min="0" value={cfg.per[c]?.participacion ?? ""} onChange={(e) => setCur(c, "participacion", e.target.value)} data-testid={`agencia-plus-part-${c}`} />
          <Label className="text-xs">Utilidad (%)</Label>
          <Input type="number" min="0" value={cfg.per[c]?.utilidad ?? ""} onChange={(e) => setCur(c, "utilidad", e.target.value)} data-testid={`agencia-plus-util-${c}`} />
        </div>))}</div>
      <p className="text-xs text-muted-foreground">Los valores no pueden superar los del grupo padre. Deja vacío para no limitar.</p>
    </div>
  );
}

function PasswordCell({ value, onRegen, testid }) {
  const [vis, setVis] = useState(false);
  return (
    <div className="flex items-center gap-1">
      <code className="text-xs font-mono px-2 py-1 rounded bg-muted min-w-[118px]" data-testid={testid}>{vis ? value : "••••••••••••"}</code>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setVis((v) => !v)} data-testid={`${testid}-toggle`}>{vis ? <EyeOff size={13} /> : <Eye size={13} />}</Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onRegen} data-testid={`${testid}-regen`}><RefreshCw size={13} /></Button>
    </div>
  );
}

function StepTaquillas({ units, setUnits, count, setCount, madre, setMadre }) {
  const upd = (i, k, v) => setUnits((u) => u.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-4" data-testid="agencia-plus-step-3">
      <div className="flex items-center gap-2"><Label>Cantidad de taquillas</Label>
        {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setCount(n)} data-testid={`agencia-plus-count-${n}`}
          className={`h-8 w-8 rounded-full text-sm font-semibold border transition-colors ${count === n ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/60"}`}>{n}</button>)}
      </div>
      <div className="space-y-2">{units.map((u, i) => (
        <div key={u.code} className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr_auto] gap-2 items-center rounded-lg border border-border p-2.5" data-testid={`agencia-plus-unit-${i}`}>
          <Input value={u.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`agencia-plus-unit-name-${i}`} />
          <code className="text-xs font-mono text-muted-foreground" data-testid={`agencia-plus-unit-code-${i}`}>{u.code}</code>
          <code className="text-xs font-mono" data-testid={`agencia-plus-unit-user-${i}`}>{u.username}</code>
          <PasswordCell value={u.password} onRegen={() => upd(i, "password", genPassword())} testid={`agencia-plus-unit-pass-${i}`} />
        </div>))}</div>
      <div className="rounded-lg border border-border p-3 space-y-3">
        <div className="flex items-center justify-between"><div>
          <div className="text-sm font-semibold">Crear usuario Taquilla Madre (Caja 00)</div>
          <div className="text-xs text-muted-foreground">Supervisión consolidada de la agencia, sin venta ni caja.</div></div>
          <Switch checked={madre.create} onCheckedChange={(v) => setMadre({ ...madre, create: v })} data-testid="agencia-plus-madre-switch" /></div>
        {madre.create && <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-center">
          <code className="text-xs font-mono" data-testid="agencia-plus-madre-user">{madre.username}</code>
          <Input value={madre.full_name} onChange={(e) => setMadre({ ...madre, full_name: e.target.value })} data-testid="agencia-plus-madre-name" />
          <PasswordCell value={madre.password} onRegen={() => setMadre({ ...madre, password: genPassword() })} testid="agencia-plus-madre-pass" /></div>}
      </div>
    </div>
  );
}

function StepResumen({ base, grupo, src, cfg, units, madre }) {
  const Row = ({ k, v, t }) => <div className="flex justify-between gap-3 py-1.5 border-b border-border/60 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right" data-testid={t}>{v}</span></div>;
  return (
    <div className="space-y-4 min-w-0" data-testid="agencia-plus-step-4">
      <div><Row k="Agencia" v={`${base.name} · ${base.code}`} t="agencia-plus-summary-agencia" />
        <Row k="Grupo" v={grupo?.name || "-"} t="agencia-plus-summary-grupo" />
        <Row k="Divisas" v={base.currencies.join(" · ")} t="agencia-plus-summary-currencies" />
        <Row k="Límite diario" v={Number(base.sales_limit_bs) > 0 ? `${base.sales_limit_bs} Bs` : "Ilimitado"} t="agencia-plus-summary-limit" />
        <Row k="Configuración" v={src ? `Heredada de ${src.name}` : `Comisión ${cfg.commission_pct || 0}%`} t="agencia-plus-summary-config" />
        <Row k="Taquillas" v={units.length} t="agencia-plus-summary-count" />
        <Row k="Caja 00" v={madre.create ? madre.username : "No"} t="agencia-plus-summary-madre" /></div>
      <div className="text-xs text-muted-foreground break-all">{units.map((u) => `${u.code} → ${u.username}`).join("  ·  ")}</div>
      <p className="text-xs text-muted-foreground">La creación es atómica: si algún elemento falla, no se crea nada.</p>
    </div>
  );
}

const credentialsText = (res, units, madre) => {
  const pw = Object.fromEntries(units.map((u) => [u.username, u.password]));
  const lines = [`AGENCIA ${res.agencia.name} (${res.agencia.code})`, ""];
  res.operators.forEach((o) => lines.push(`Taquilla ${o.taquilla_code} · ${o.full_name} · Usuario: ${o.username} · Clave: ${pw[o.username]}`));
  if (res.taquilla_madre) lines.push(`Caja 00 · ${res.taquilla_madre.full_name} · Usuario: ${res.taquilla_madre.username} · Clave: ${madre.password}`);
  lines.push("", "Claves temporales: cambiar en el primer ingreso.");
  return lines.join("\n");
};

function SuccessCard({ res, units, madre, onClose }) {
  const text = credentialsText(res, units, madre);
  const copy = async () => { await navigator.clipboard.writeText(text); toast.success("Credenciales copiadas"); };
  const print = () => {
    const w = window.open("", "_blank", "width=600,height=700");
    if (!w) return toast.error("Habilita las ventanas emergentes para imprimir");
    w.document.write(`<pre style="font:13px monospace;white-space:pre-wrap">${text.replace(/</g, "&lt;")}</pre>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <div className="space-y-4" data-testid="agencia-plus-success">
      <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={20} /><span className="font-semibold">Agencia {res.agencia.code} creada completa</span></div>
      <pre className="text-xs font-mono rounded-lg border border-border bg-muted/40 p-3 whitespace-pre-wrap max-h-72 overflow-auto" data-testid="agencia-plus-credentials">{text}</pre>
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" onClick={copy} data-testid="agencia-plus-copy-all"><Copy size={15} className="mr-1.5" />Copiar Todo</Button>
        <Button variant="outline" onClick={print} data-testid="agencia-plus-print"><Printer size={15} className="mr-1.5" />Imprimir Resumen</Button>
        <Button onClick={onClose} data-testid="agencia-plus-finish">Listo</Button>
      </div>
    </div>
  );
}

const toPayload = (base, cfg, units, madre) => {
  const per = Object.fromEntries(Object.entries(cfg.per).filter(([c]) => base.currencies.includes(c)).map(([c, v]) => [c,
    Object.fromEntries(Object.entries(v).filter(([, x]) => x !== "" && x != null).map(([k, x]) => [k, Number(x)]))]).filter(([, v]) => Object.keys(v).length));
  const cloning = base.clone_from !== NO_CLONE;
  return {
    agencia: { name: base.name.trim(), code: base.code.trim(), grupo_id: base.grupo_id, currencies: base.currencies,
      sales_limit_bs: Number(base.sales_limit_bs) > 0 ? Number(base.sales_limit_bs) : null },
    clone_from_agencia_id: cloning ? base.clone_from : null,
    config: cloning ? null : { commission_pct: Number(cfg.commission_pct) || 0, config: per },
    taquillas: units.map((u) => ({ name: u.name.trim(), code: u.code })),
    operators: units.map((u) => ({ username: u.username, password: u.password, full_name: u.full_name, taquilla_code_ref: u.code })),
    taquilla_madre: madre.create ? { create_user: true, username: madre.username, password: madre.password, full_name: madre.full_name } : { create_user: false },
  };
};

export default function AgenciaPlusWizardDialog({ open, onOpenChange, grupos, agencias }) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [base, setBase] = useState(EMPTY_BASE);
  const [cfg, setCfg] = useState({ commission_pct: 0, per: {} });
  const [count, setCount] = useState(2);
  const [units, setUnits] = useState(() => buildUnits("", 2));
  const [madre, setMadre] = useState(EMPTY_MADRE);
  const [res, setRes] = useState(null);

  useEffect(() => { setUnits((prev) => buildUnits(base.code, count, prev)); }, [base.code, count]);
  useEffect(() => { setMadre((m) => ({ ...m, username: `${(base.code || "age").toLowerCase()}_caja00` })); }, [base.code]);
  useEffect(() => { if (!open) { setStep(0); setBase(EMPTY_BASE); setCfg({ commission_pct: 0, per: {} }); setCount(2); setMadre({ ...EMPTY_MADRE, password: genPassword() }); setRes(null); } }, [open]);

  const grupo = grupos.find((g) => g.id === base.grupo_id);
  const src = agencias.find((a) => a.id === base.clone_from);
  const canNext = useMemo(() => [
    base.name.trim() && base.code.trim() && base.grupo_id && base.currencies.length > 0,
    true, units.length > 0 && (!madre.create || madre.full_name.trim()), true,
  ][step], [step, base, units, madre]);

  const create = useMutation({
    mutationFn: async () => (await api.post("/agencias/onboarding-plus", toPayload(base, cfg, units, madre))).data,
    onSuccess: (r) => { setRes(r); toast.success(`Agencia ${r.agencia.code} creada`); qc.invalidateQueries({ queryKey: ["agencias"] }); qc.invalidateQueries({ queryKey: ["taquillas"] }); },
    onError: (e) => { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Error al crear la agencia"); },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] max-h-[92vh] overflow-y-auto" data-testid="agencia-plus-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap size={18} className="text-primary" />Agencia Plus</DialogTitle>
          <DialogDescription>Alta guiada y atómica: agencia, configuración, taquillas y operadores en un solo paso.</DialogDescription>
        </DialogHeader>
        {res ? <SuccessCard res={res} units={units} madre={madre} onClose={() => onOpenChange(false)} /> : (<>
          <ol className="grid grid-cols-4 gap-1.5" data-testid="agencia-plus-stepper">{STEPS.map((s, i) => (
            <li key={s} className={`rounded-md px-2 py-1.5 text-[11px] sm:text-xs font-medium border ${i === step ? "border-primary bg-primary/10 text-foreground" : i < step ? "border-primary/40 text-primary" : "border-border text-muted-foreground"}`} data-testid={`agencia-plus-stepper-${i + 1}`}>
              <span className="inline-flex items-center gap-1">{i < step ? <Check size={12} /> : `${i + 1}.`} <span className="hidden sm:inline">{s}</span></span></li>))}</ol>
          <div className="min-h-[260px] pt-1">
            {step === 0 && <StepBase base={base} setBase={setBase} grupos={grupos} agencias={agencias} />}
            {step === 1 && <StepConfig base={base} cfg={cfg} setCfg={setCfg} agencias={agencias} />}
            {step === 2 && <StepTaquillas units={units} setUnits={setUnits} count={count} setCount={setCount} madre={madre} setMadre={setMadre} />}
            {step === 3 && <StepResumen base={base} grupo={grupo} src={src} cfg={cfg} units={units} madre={madre} />}
          </div>
          <div className="flex justify-between gap-2 pt-2 border-t border-border">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="agencia-plus-back"><ChevronLeft size={16} className="mr-1" />Atrás</Button>
            {step < 3
              ? <Button disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="agencia-plus-next">Siguiente<ChevronRight size={16} className="ml-1" /></Button>
              : <Button disabled={create.isPending} onClick={() => create.mutate()} data-testid="agencia-plus-submit"><Zap size={15} className="mr-1.5" />{create.isPending ? "Creando…" : "Crear Agencia Completa"}</Button>}
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
